import { summarizeAdherence } from './adherence.js';
import { roundHalfUp, isoDate } from './math.js';
import { expandSlots } from './schedule.js';
import { treatmentPlanAt } from './treatment-lifecycle.js';
/** 계획에서 하루 평균 투약 횟수. 필요할 때(asNeeded)는 계산하지 않는다. */
export function dosesPerDay(schedule) {
    if (schedule.type === 'asNeeded')
        return null;
    const times = schedule.times.length;
    if (!times)
        return null;
    if (schedule.type === 'daily')
        return times;
    if (schedule.type === 'weekly')
        return schedule.weekdays.length ? (times * schedule.weekdays.length) / 7 : null;
    return schedule.everyDays > 0 ? times / schedule.everyDays : null;
}
/**
 * K19 — 보호자가 센 재고와 투약 기록을 이어 남은 일수를 계산한다. 저장은 센 값 그대로 두고 화면 계산만 한다.
 * 센 시각 뒤의 했어요·일부 기록만 뺀다. 시각이 없는 옛 기록은 다음 날부터 계산한다.
 */
export function estimateSupplyDays(supply, treatment, logs, now = Date.now()) {
    if (!supply.treatmentId || supply.treatmentId !== treatment.id || !supply.perDoseQuantity || supply.perDoseQuantity <= 0)
        return null;
    const from = supply.checkedAt ?? Date.parse(`${supply.checkedOn}T23:59:59.999+09:00`);
    if (!Number.isFinite(from) || from > now || (supply.checkedAt != null && isoDate(from, 'Asia/Seoul') !== supply.checkedOn))
        return null;
    const usedDoses = logs.filter((log) => log.treatmentId === treatment.id && !log.deletedAt && !log.duplicateOf && (log.status === 'given' || log.status === 'partial') && log.recordedAt > from && log.recordedAt <= now).length;
    const remaining = Math.max(0, roundHalfUp(supply.remainingQuantity - usedDoses * supply.perDoseQuantity, 2));
    const perDay = dosesPerDay(treatmentPlanAt(treatment, now).schedule);
    const days = perDay ? Math.floor(remaining / (supply.perDoseQuantity * perDay)) : null;
    return { remaining, usedDoses, days, checkedOn: supply.checkedOn, unitLabel: supply.unitLabel };
}
/** K22 — 지난 n일 계획 회차 대비 기록한 회차. "복용률"이 아니라 기록률이다(안 적은 것과 안 준 것을 구분하지 못함). */
export function adherenceWindow(treatment, logs, windowDays, now = Date.now(), timeZone = 'Asia/Seoul') {
    const to = isoDate(now, timeZone);
    const fromDate = new Date(`${to}T00:00:00Z`);
    fromDate.setUTCDate(fromDate.getUTCDate() - (windowDays - 1));
    const from = fromDate.toISOString().slice(0, 10);
    const slots = expandSlots(treatment, timeZone, from, to).filter((slot) => slot.epoch <= now);
    const mine = logs.filter((log) => log.treatmentId === treatment.id && !log.deletedAt && !log.duplicateOf);
    const summary = summarizeAdherence(slots, mine);
    const recorded = new Set(mine.filter((log) => log.scheduledAt != null).map((log) => log.scheduledAt));
    const missingDates = [...new Set(slots.filter((slot) => !recorded.has(slot.epoch)).map((slot) => isoDate(slot.epoch, timeZone)))].sort();
    return { ...summary, windowDays, missingDates };
}
/** K24 — 지금 먹는 약 목록 텍스트. 이름·용량 문구·시각·시작일만, 보호자가 적은 그대로. */
export function buildCurrentMedsText(petName, treatments, today, now = Date.now()) {
    const active = treatments.filter((row) => !row.deletedAt).map((row) => ({ row, plan: treatmentPlanAt(row, now) })).filter(({ plan }) => plan.state === 'active');
    if (!active.length)
        return null;
    const when = (schedule) => schedule.type === 'asNeeded' ? '필요할 때' : `${schedule.type === 'daily' ? '매일' : schedule.type === 'weekly' ? `매주 ${schedule.weekdays.map((day) => '일월화수목금토'[day]).join('·')}` : `${schedule.everyDays}일마다`} ${schedule.times.join(', ')}`;
    return [
        `${petName} · 지금 먹는 약 (${today.slice(5).replace('-', '/')} 기준)`,
        ...active.map(({ plan }) => `- ${plan.name}${plan.doseText ? ` · ${plan.doseText}` : ''} · ${when(plan.schedule)}${plan.startDate ? ` · ${plan.startDate}부터` : ''}`),
        '보호자가 처방대로 적어 둔 내용이에요.',
    ].join('\n');
}
